import type { EventSource } from "../api/schemas";
import type { IdGenerator } from "../domain/ids";
import { rememberProject } from "../domain/projects";
import { toEpochSeconds } from "../domain/time";
import type { EpochSeconds } from "../domain/time";
import type { LocalStore } from "./LocalStore";
import type { LocalAttribution } from "./model";

/** Dependencies of {@link recordProjectSwitch}. */
export interface RecordAttributionDependencies {
  readonly store: LocalStore;
  readonly ids: IdGenerator;
  /** Device clock in epoch milliseconds. */
  readonly nowMs: () => number;
}

/** Input of {@link recordProjectSwitch}. */
export interface RecordSwitchInput {
  readonly ownerSubject: string;
  /** The project, or `null` to stop attributing. */
  readonly projectId: string | null;
  readonly source: EventSource;
  /** Whether the device is offline (archived projects are then accepted and marked, PINV-P03). */
  readonly offline: boolean;
  /** The activity (T-184); offline one no longer allowed is accepted and marked (PINV-P08). */
  readonly activityId?: string | null;
  /** The instant of the switch, for example the clock-in it belongs to; the device clock otherwise. */
  readonly occurredAt?: EpochSeconds;
  /** The clock event the switch belongs to; it is sent only after that event was accepted. */
  readonly followsEventId?: string;
}

/**
 * Records "switch project" locally first (T-124, spec 6.5.4). It is not a clock event: the
 * working time stays unchanged (PINV-P01) and it is sent after the clock events of the same
 * synchronization. The project becomes the most recently used one.
 *
 * @param dependencies - Store, identifier generator and clock.
 * @param input - What to record.
 * @returns The stored attribution.
 */
export async function recordProjectSwitch(
  dependencies: RecordAttributionDependencies,
  input: RecordSwitchInput,
): Promise<LocalAttribution> {
  const nowMs = dependencies.nowMs();
  const attribution: LocalAttribution = {
    id: dependencies.ids.next(),
    ownerSubject: input.ownerSubject,
    kind: "Switch",
    projectId: input.projectId,
    occurredAt: input.occurredAt ?? toEpochSeconds(nowMs),
    rangeStart: null,
    rangeEnd: null,
    supersedesAttributionId: null,
    note: null,
    source: input.source,
    recordedOffline: input.offline,
    createdAtMs: nowMs,
    syncStatus: "pending",
    syncedAtMs: null,
    attempts: 0,
    error: null,
    hints: [],
    followUp: "None",
    ...withActivity(input.projectId, input.activityId),
    ...(input.followsEventId === undefined ? {} : { followsEventId: input.followsEventId }),
  };
  await dependencies.store.addAttribution(attribution);

  if (input.projectId !== null) {
    await rememberProjectUse(dependencies.store, input.ownerSubject, input.projectId);
  }

  return attribution;
}

/**
 * Returns the activity part of an attribution: only with a project and an activity, so entries
 * without one keep their former shape.
 *
 * @param projectId - The project.
 * @param activityId - The activity.
 * @returns The property to spread.
 */
function withActivity(
  projectId: string | null,
  activityId: string | null | undefined,
): { readonly activityId?: string } {
  const part =
    projectId === null || activityId === null || activityId === undefined ? {} : { activityId };
  return part;
}

/**
 * Adds a project to the recently used projects of a person.
 *
 * @param store - The local store.
 * @param ownerSubject - Owner key.
 * @param projectId - The project.
 */
export async function rememberProjectUse(
  store: LocalStore,
  ownerSubject: string,
  projectId: string,
): Promise<void> {
  const preferences = await store.getProjectPreferences(ownerSubject);
  await store.putProjectPreferences(rememberProject(preferences, projectId));
}

/** Input of {@link recordProjectRange}. */
export interface RecordRangeInput {
  readonly ownerSubject: string;
  /** The project, or `null` to remove the attribution of the range. */
  readonly projectId: string | null;
  readonly start: EpochSeconds;
  /** End of the range (exclusive). */
  readonly end: EpochSeconds;
  readonly source: EventSource;
  readonly offline: boolean;
  /** The activity (T-184). */
  readonly activityId?: string | null;
}

/**
 * Records the attribution of a time range locally first, for example from the day overview
 * (T-125). A range wins over switches (spec 6.5.3) and never changes the working time.
 *
 * @param dependencies - Store, identifier generator and clock.
 * @param input - What to record.
 * @returns The stored attribution.
 */
export async function recordProjectRange(
  dependencies: RecordAttributionDependencies,
  input: RecordRangeInput,
): Promise<LocalAttribution> {
  const nowMs = dependencies.nowMs();
  const attribution: LocalAttribution = {
    id: dependencies.ids.next(),
    ownerSubject: input.ownerSubject,
    kind: "Range",
    projectId: input.projectId,
    occurredAt: null,
    rangeStart: input.start,
    rangeEnd: input.end,
    supersedesAttributionId: null,
    note: null,
    source: input.source,
    recordedOffline: input.offline,
    createdAtMs: nowMs,
    syncStatus: "pending",
    syncedAtMs: null,
    attempts: 0,
    error: null,
    hints: [],
    followUp: "None",
    ...withActivity(input.projectId, input.activityId),
  };
  await dependencies.store.addAttribution(attribution);

  if (input.projectId !== null) {
    await rememberProjectUse(dependencies.store, input.ownerSubject, input.projectId);
  }

  return attribution;
}
